/* eslint-disable @typescript-eslint/no-explicit-any */
import queryString from 'query-string';
import { Store } from '../state/ApiContext';
import { Cache } from '../state/CachingContext';
import { IJwt, ITokens, ITokensExternal, IUserOADetails } from '../interfaces';
import ApiHelper from './ApiHelper';
import { NavigateFunction } from 'react-router-dom';

export default class SiteHelper {
  static parseUrl(): queryString.ParsedQuery<string> {
    const url = window.location.search;
    const parsedUrl = queryString.parse(url);

    // Parse keys to lowercase while values stay case sensitive
    const caseParsed = Object.fromEntries(Object.entries(parsedUrl).map(([k, v]) => [k.toLowerCase(), v]));
    return caseParsed;
  }

  static stripQueryString(): void {
    window.history.replaceState(window.history.state, window.document.title, window.location.pathname);
  }

  static deepCopy<T>(object: T): T {
    return JSON.parse(JSON.stringify(object));
  }

  static parseJwt(token: string): IJwt {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    const parsedToken = JSON.parse(jsonPayload);

    return parsedToken as IJwt;
  }

  static getUserOADetails = (
    email: string,
    session: Cache,
    setCache: (key: string, value: unknown) => void,
    getRequest: Store['getRequest'],
    setLoader?: ((isLoading: boolean) => void) | undefined,
  ): Promise<IUserOADetails> =>
    new Promise<IUserOADetails>((resolve, reject) => {
      getRequest<{ data: IUserOADetails }>(
        'User/oaId/Details',
        [
          { name: 'email', value: email },
          { name: 'requestId', value: session.requestid as string },
          { name: 'organisationApplicationGuid', value: session.organisationsapplicationsguid as string },
        ],
        setLoader,
      )
        .then((result) => {
          if (result.data.returnUrl) setCache('returnurl', result.data.returnUrl);
          resolve(result.data);
        })
        .catch(() => reject());
    });

  static refreshToken(
    tokens: ITokens,
    session: Cache,
    setTokens: (tokens: ITokens) => void,
    clearCache: (key?: string | undefined) => void,
    postRequest: Store['postRequest'],
    clear: () => void,
  ): void {
    const parsedToken = this.parseJwt(tokens.accessToken);
    // If accessToken has expired and a valid refreshToken is available
    if (parsedToken.amr === 'mfa' && tokens.refreshToken) {
      if (session.returnurl && (session.organisationsapplicationsguid || session.my) && session.requestid) {
        // Refresh token for external login
        postRequest<{ data: ITokensExternal }>('Token/RefreshExternal', {
          requestid: session.requestid,
          ...(session.returnurl === 'my'
            ? { my: true }
            : { organisationApplicationGuid: session.organisationsapplicationsguid }),
          authenticationType: 'web',
          tokens: {
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
          },
        })
          .then((result) => {
            if (session.returnurl) {
              setTokens(result.data.tokens);
              if (session.returnurl === 'my') {
                ApiHelper.redirectToMyAtiQxID(result.data);
              } else {
                ApiHelper.redirectToExternal(
                  session.returnurl as string,
                  result.data,
                  session.source === 'app' ? session.source : undefined,
                );
              }
              clearCache();
            }
            // Logout when there is no return url, I can't redirect to nothing
            else clear();
          })
          .catch(
            // When this call errors it probably means the refreshToken has expired. In this case login is required.
            () => clear(),
          );
      } else {
        clear();
      }
    } else {
      clear();
    }
  }

  static handleRedirectExternal(
    session: any,
    authData: any,
    setEmail: (email: string) => void,
    clearCache: (key?: string | undefined) => void,
    setCache: (key: string, value: unknown) => void,
    navigate: NavigateFunction,
    skipPhoneVerification?: boolean,
  ): void {
    if (authData) {
      if (session && (skipPhoneVerification || session.verifiedPhone || session.verifiedPhone === undefined)) {
        setEmail(session.email);

        if (session.organisationsapplicationsguid && session.returnurl) {
          ApiHelper.redirectToExternal(
            session.returnurl,
            authData,
            session.source === 'app' ? session.source : undefined,
          );
        } else if (session && session.my) {
          ApiHelper.redirectToMyAtiQxID(authData);
        }

        clearCache();
      } else if (session && session.verifiedPhone === false) {
        setCache('authData', authData);
        navigate('/VerifyPhone');
      }
    } else {
      navigate('/');
    }
  }
}
